import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import DOMPurify from 'dompurify';
import { useToast } from '../../Context/ToastContext';

// Import your icons
import ChatBotIcon from '../../Images/chat-bot-icon.svg';
import SystemIcon from '../../Images/system-icon.svg';
import UserChatIcon from '../../Images/user-chat-icon.svg';
import ExpertChatIcon from '../../Images/expert-chat-icon.svg';
import WidgetUploadIcon from '../../Images/widget-upload-icon.svg';
import PDFIcon from '../../Images/pdf-icon.svg';
import FileIcon from '../../Images/file-icon.svg';
import XLSXIcon from '../../Images/xlsx-icon.svg';

import JSONIcon from '../../Images/json-icon.svg';
import MDIcon from '../../Images/md-icon.svg';
import PPTXIcon from '../../Images/pptx-icon.svg';
import ExcelIcon from '../../Images/excel-icon.svg';
import WordIcon from '../../Images/word-icon.svg';
import ImageIcon from '../../Images/image-icon.svg';
import XMLIcon from '../../Images/xml-icon.svg';

// The widget engine's control markers are stored with the AI's reply ("CONVERSATION:[DONE]",
// "LANGUAGE:[en]", progress/classification tags); the widget reads them, people should not see them.
const stripEngineMarkers = (text) => {
  if (typeof text !== 'string') return text;
  // Both forms of each marker: the model sometimes drops the brackets ("LANGUAGE:en",
  // "CONVERSATION:DONE" — a YK widget conversation on 2026-10-20 showed "LANGUAGE:en").
  // Upper-case and code-shaped only, so ordinary words ("our conversation: …",
  // "Language: English") are never eaten.
  return text
    .replace(/\bCONVERSATION:\s*\[?\s*DONE\s*\]?/g, '')
    .replace(/\bCONVERSATION:(?=\s*(?:<|$|LANGUAGE\b))/gm, '') // dangling, its [DONE] stripped upstream
    .replace(/\[DONE\]/gi, '')
    // Case-sensitive on "LANGUAGE": with the i flag it also ate "language: en" from "[SYSTEM] User selected language: en."
    .replace(/(?:<strong>\s*)?\bLANGUAGE:\s*(?:<\/strong>\s*)?(?:\[\s*[a-zA-Z]{2}(?:-[a-zA-Z]{2,4})?\s*\]|[a-zA-Z]{2}(?:-[a-zA-Z]{2,4})?(?![a-zA-Z]))/g, '')
    .replace(/\[PROGRESS:\s*\d+\s*\/\s*\d+\]/gi, '')
    .replace(/\[CLASSIFICATION:[^\]]*\]/gi, '')
    .replace(/<p>\s*<\/p>/gi, '')
    .trim();
};

// Helper function to detect signature request content by structure
const isSignatureRequestContent = (content) => {
  if (!content || typeof content !== 'string') return false;
  try {
    const parsed = JSON.parse(content);
    return parsed && parsed.requestCode && parsed.purpose && typeof parsed.status === 'string';
  } catch {
    return false;
  }
};

// Helper function to detect a document-request message by structure
const isDocumentRequestContent = (content) => {
  if (!content || typeof content !== 'string') return false;
  try {
    const parsed = JSON.parse(content);
    return parsed && parsed.requestId && Array.isArray(parsed.items);
  } catch {
    return false;
  }
};

// Helper function to detect service questionnaire content by structure
const isServiceQuestionnaireContent = (content) => {
  if (!content || typeof content !== 'string') return false;
  try {
    const parsed = JSON.parse(content);
    return parsed && parsed.sessionId && parsed.serviceType && parsed.sessionUrl;
  } catch {
    return false;
  }
};

// Styled Components
const MessageContainer = styled.div`
  display: flex;
  justify-content: ${(props) => (props.type === 'expert' ? 'flex-end' : 'flex-start')};
  align-items: flex-start;
  margin-bottom: 8px;
  background: transparent;
  width: 100%;
  padding: 4px 0;
`;

const MessageBubble = styled.div`
  background: ${(props) => {
    switch (props.type) {
      case 'user':
        return 'linear-gradient(135deg, rgba(71, 85, 105, 0.95) 0%, rgba(51, 65, 85, 0.98) 100%)';
      case 'gpt':
        return 'linear-gradient(135deg, rgba(59, 130, 246, 0.25) 0%, rgba(37, 99, 235, 0.3) 100%)';
      case 'system':
        return 'linear-gradient(135deg, rgba(168, 85, 247, 0.25) 0%, rgba(147, 51, 234, 0.3) 100%)';
      case 'expert':
        return 'linear-gradient(135deg, rgba(16, 185, 129, 0.25) 0%, rgba(5, 150, 105, 0.3) 100%)';
      case 'widget-upload':
        return 'linear-gradient(135deg, rgba(245, 158, 11, 0.25) 0%, rgba(217, 119, 6, 0.3) 100%)';
      case 'video-call':
        return 'linear-gradient(135deg, rgba(99, 102, 241, 0.25) 0%, rgba(79, 70, 229, 0.3) 100%)';
      default:
        return 'linear-gradient(135deg, rgba(59, 130, 246, 0.25) 0%, rgba(37, 99, 235, 0.3) 100%)';
    }
  }};
  
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  
  border: 1px solid ${(props) => {
    switch (props.type) {
      case 'user':
        return 'rgba(148, 163, 184, 0.3)';
      case 'gpt':
        return 'rgba(59, 130, 246, 0.4)';
      case 'system':
        return 'rgba(168, 85, 247, 0.4)';
      case 'expert':
        return 'rgba(16, 185, 129, 0.4)';
      case 'widget-upload':
        return 'rgba(245, 158, 11, 0.4)';
      case 'video-call':
        return 'rgba(99, 102, 241, 0.4)';
      default:
        return 'rgba(59, 130, 246, 0.4)';
    }
  }};
  
  box-shadow: ${(props) => {
    switch (props.type) {
      case 'user':
        return '0 4px 16px rgba(0, 0, 0, 0.2)';
      case 'gpt':
        return '0 4px 16px rgba(59, 130, 246, 0.15)';
      case 'system':
        return '0 4px 16px rgba(168, 85, 247, 0.15)';
      case 'expert':
        return '0 4px 16px rgba(16, 185, 129, 0.15)';
      case 'widget-upload':
        return '0 4px 16px rgba(245, 158, 11, 0.15)';
      case 'video-call':
        return '0 4px 16px rgba(99, 102, 241, 0.15)';
      default:
        return '0 4px 16px rgba(59, 130, 246, 0.15)';
    }
  }};
  
  color: rgba(255, 255, 255, 0.95);
  padding: 14px 16px;
  border-radius: 14px;
  max-width: 70%;
  min-width: 50%;
  margin-left: ${(props) => (props.type === 'expert' ? '10px' : '0')};
  margin-right: ${(props) => (props.type === 'expert' ? '0' : '10px')};
  word-wrap: break-word;
  overflow-wrap: break-word;
  line-height: 1.6;
  position: relative;
  font-size: 14px;
  z-index: 0;
  text-align: left;
  
  transition: all 0.2s ease;

  .message-content {
    position: relative;
    line-height: inherit;
  }

  .message-content mark {
    background-color: rgba(80, 140, 220, 0.3);
    color: inherit;
    padding: 0;
    margin: 0;
    border-radius: 2px;
  }

  .message-content mark:hover {
    background-color: rgba(80, 140, 220, 0.4);
  }

  /* Table styling for HTML content */
  .message-content table {
    width: 100%;
    border-collapse: collapse;
    margin: 12px 0;
    font-size: 13px;
  }

  .message-content th,
  .message-content td {
    padding: 8px 12px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    text-align: left;
  }

  .message-content th {
    background: rgba(255, 255, 255, 0.1);
    font-weight: 600;
  }

  .message-content tr:nth-child(even) {
    background: rgba(255, 255, 255, 0.05);
  }

  /* Heading styles */
  .message-content h1, .message-content h2, .message-content h3, 
  .message-content h4, .message-content h5, .message-content h6 {
    margin: 12px 0 8px 0;
    font-weight: 600;
  }

  .message-content h1 { font-size: 18px; }
  .message-content h2 { font-size: 16px; }
  .message-content h3 { font-size: 15px; }

  /* List styles */
  .message-content ul, .message-content ol {
    margin: 8px 0;
    padding-left: 20px;
  }

  .message-content li {
    margin: 4px 0;
  }

  /* Horizontal rule */
  .message-content hr {
    border: none;
    border-top: 1px solid rgba(255, 255, 255, 0.2);
    margin: 12px 0;
  }
`;

const Icon = styled.img`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  margin-right: ${(props) => (props.type === 'expert' ? '0px' : '10px')};
  margin-left: ${(props) => (props.type === 'expert' ? '10px' : '0x')};
  background-color: transparent;
  padding: 2px;
`;

const FilePreview = styled.div`
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  background: none;
  
  a {
    display: flex;
    align-items: center;
    text-decoration: none;
    color: rgba(255, 255, 255, 0.9);
    margin-bottom: 5px;
    background: none;
    padding: 0;

    img {
      width: 32px;
      height: 32px;
      margin-right: 8px;
    }

    span {
      word-break: break-all;
      background: none;
    }

    &:hover {
      color: #4a90e2;
      text-decoration: underline;
    }

    &:visited {
      color: rgba(255, 255, 255, 0.5);
    }
  }
`;

const SenderName = styled.div`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.3px;
  color: ${(props) => {
    switch (props.type) {
      case 'user':
        return '#cbd5e1'; // Slate-300
      case 'gpt':
        return '#93c5fd'; // Blue-300
      case 'system':
        return '#d8b4fe'; // Purple-300
      case 'expert':
        return '#6ee7b7'; // Emerald-300
      case 'widget-upload':
        return '#fcd34d'; // Amber-300
      case 'video-call':
        return '#a5b4fc'; // Indigo-300
      default:
        return '#93c5fd';
    }
  }};
  margin-bottom: 8px;
  opacity: 1;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
`;

const VideoCallBubble = styled.div`
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(37, 99, 235, 0.35) 100%);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  color: #fff;
  padding: 16px 20px;
  border-radius: 14px;
  max-width: 300px;
  min-width: 250px;
  margin-left: ${(props) => (props.type === 'expert' ? '10px' : '0')};
  margin-right: ${(props) => (props.type === 'expert' ? '0' : '10px')};
  border: 1px solid rgba(59, 130, 246, 0.5);
  box-shadow: 0 4px 16px rgba(59, 130, 246, 0.2);
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
  
  &:hover {
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.4) 0%, rgba(37, 99, 235, 0.45) 100%);
    border-color: rgba(59, 130, 246, 0.7);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(59, 130, 246, 0.3);
  }
  
  &:active {
    transform: translateY(0px);
  }
`;

const WhiteboardBubble = styled.div`
  background: linear-gradient(135deg, rgba(168, 85, 247, 0.3) 0%, rgba(147, 51, 234, 0.35) 100%);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  color: #fff;
  padding: 16px 20px;
  border-radius: 14px;
  max-width: 300px;
  min-width: 250px;
  margin-left: ${(props) => (props.type === 'expert' ? '10px' : '0')};
  margin-right: ${(props) => (props.type === 'expert' ? '0' : '10px')};
  border: 1px solid rgba(168, 85, 247, 0.5);
  box-shadow: 0 4px 16px rgba(168, 85, 247, 0.2);
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
  
  &:hover {
    background: linear-gradient(135deg, rgba(168, 85, 247, 0.4) 0%, rgba(147, 51, 234, 0.45) 100%);
    border-color: rgba(168, 85, 247, 0.7);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(168, 85, 247, 0.3);
  }
  
  &:active {
    transform: translateY(0px);
  }
`;

const PaymentRequestBubble = styled.div`
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.3) 0%, rgba(5, 150, 105, 0.35) 100%);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  color: #fff;
  padding: 16px 20px;
  border-radius: 14px;
  max-width: 320px;
  min-width: 280px;
  margin-left: ${(props) => (props.type === 'expert' ? '10px' : '0')};
  margin-right: ${(props) => (props.type === 'expert' ? '0' : '10px')};
  border: 1px solid rgba(16, 185, 129, 0.5);
  box-shadow: 0 4px 16px rgba(16, 185, 129, 0.2);
  transition: all 0.2s ease;
`;

const PaymentAmount = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #10b981;
  margin: 8px 0;
`;

const PaymentDescription = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 12px;
  line-height: 1.4;
`;

const PaymentExpiry = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 12px;
`;

const PaymentButton = styled.a`
  display: inline-block;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #fff;
  padding: 10px 16px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 13px;
  text-decoration: none;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
  }
`;

const SignatureRequestBubble = styled.div`
  background: linear-gradient(135deg, rgba(236, 72, 153, 0.3) 0%, rgba(219, 39, 119, 0.35) 100%);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  color: #fff;
  padding: 16px 20px;
  border-radius: 14px;
  max-width: 320px;
  min-width: 280px;
  margin-left: ${(props) => (props.type === 'expert' ? '10px' : '0')};
  margin-right: ${(props) => (props.type === 'expert' ? '0' : '10px')};
  border: 1px solid rgba(236, 72, 153, 0.5);
  box-shadow: 0 4px 16px rgba(236, 72, 153, 0.2);
  transition: all 0.2s ease;
`;

const SignaturePurpose = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #ec4899;
  margin: 8px 0;
`;

const SignatureDescription = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 12px;
  line-height: 1.4;
`;

const SignatureStatus = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 12px;
  background: ${(props) => {
    switch (props.status) {
      case 'signed': return 'rgba(16, 185, 129, 0.3)';
      case 'declined': return 'rgba(239, 68, 68, 0.3)';
      case 'expired': return 'rgba(156, 163, 175, 0.3)';
      default: return 'rgba(245, 158, 11, 0.3)';
    }
  }};
  color: ${(props) => {
    switch (props.status) {
      case 'signed': return '#10b981';
      case 'declined': return '#ef4444';
      case 'expired': return '#9ca3af';
      default: return '#f59e0b';
    }
  }};
  border: 1px solid ${(props) => {
    switch (props.status) {
      case 'signed': return 'rgba(16, 185, 129, 0.5)';
      case 'declined': return 'rgba(239, 68, 68, 0.5)';
      case 'expired': return 'rgba(156, 163, 175, 0.5)';
      default: return 'rgba(245, 158, 11, 0.5)';
    }
  }};
`;

// Modern SVG icons for services
const RealEstateIconSvg = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
    <polyline points="9 22 9 12 15 12 15 22"></polyline>
  </svg>
);

const EmploymentIconSvg = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
  </svg>
);

const EnglishAcademyIconSvg = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
  </svg>
);

const DefaultServiceIconSvg = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
    <polyline points="14 2 14 8 20 8"></polyline>
    <line x1="16" y1="13" x2="8" y2="13"></line>
    <line x1="16" y1="17" x2="8" y2="17"></line>
    <polyline points="10 9 9 9 8 9"></polyline>
  </svg>
);

// Helper function to get service icon by type
const getServiceIconComponent = (serviceType) => {
  switch (serviceType) {
    case 'real_estate':
      return <RealEstateIconSvg />;
    case 'employment':
      return <EmploymentIconSvg />;
    case 'english_academy':
      return <EnglishAcademyIconSvg />;
    default:
      return <DefaultServiceIconSvg />;
  }
};

// Small icon version for sender name
const RealEstateIconSmall = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginRight: '4px' }}>
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
    <polyline points="9 22 9 12 15 12 15 22"></polyline>
  </svg>
);

const EmploymentIconSmall = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginRight: '4px' }}>
    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
  </svg>
);

const EnglishAcademyIconSmall = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginRight: '4px' }}>
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
  </svg>
);

const DefaultServiceIconSmall = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginRight: '4px' }}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
    <polyline points="14 2 14 8 20 8"></polyline>
    <line x1="16" y1="13" x2="8" y2="13"></line>
    <line x1="16" y1="17" x2="8" y2="17"></line>
  </svg>
);

// Helper function to get small service icon by type
const getServiceIconSmall = (serviceType) => {
  switch (serviceType) {
    case 'real_estate':
      return <RealEstateIconSmall />;
    case 'employment':
      return <EmploymentIconSmall />;
    case 'english_academy':
      return <EnglishAcademyIconSmall />;
    default:
      return <DefaultServiceIconSmall />;
  }
};

// Service Questionnaire Bubble Styles
const ServiceQuestionnaireBubble = styled.div`
  background: ${(props) => {
    switch (props.serviceType) {
      case 'real_estate':
        return 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(37, 99, 235, 0.35) 100%)';
      case 'employment':
        return 'linear-gradient(135deg, rgba(251, 146, 60, 0.3) 0%, rgba(234, 88, 12, 0.35) 100%)';
      case 'english_academy':
        return 'linear-gradient(135deg, rgba(139, 92, 246, 0.3) 0%, rgba(124, 58, 237, 0.35) 100%)';
      default:
        return 'linear-gradient(135deg, rgba(251, 146, 60, 0.3) 0%, rgba(234, 88, 12, 0.35) 100%)';
    }
  }};
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  color: #fff;
  padding: 18px 22px;
  border-radius: 16px;
  max-width: 340px;
  min-width: 300px;
  margin-left: ${(props) => (props.type === 'expert' ? '10px' : '0')};
  margin-right: ${(props) => (props.type === 'expert' ? '0' : '10px')};
  border: 1px solid ${(props) => {
    switch (props.serviceType) {
      case 'real_estate': return 'rgba(59, 130, 246, 0.5)';
      case 'employment': return 'rgba(251, 146, 60, 0.5)';
      case 'english_academy': return 'rgba(139, 92, 246, 0.5)';
      default: return 'rgba(251, 146, 60, 0.5)';
    }
  }};
  box-shadow: ${(props) => {
    switch (props.serviceType) {
      case 'real_estate': return '0 4px 20px rgba(59, 130, 246, 0.25)';
      case 'employment': return '0 4px 20px rgba(251, 146, 60, 0.25)';
      case 'english_academy': return '0 4px 20px rgba(139, 92, 246, 0.25)';
      default: return '0 4px 20px rgba(251, 146, 60, 0.25)';
    }
  }};
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${(props) => {
      switch (props.serviceType) {
        case 'real_estate': return '0 8px 32px rgba(59, 130, 246, 0.35)';
        case 'employment': return '0 8px 32px rgba(251, 146, 60, 0.35)';
        case 'english_academy': return '0 8px 32px rgba(139, 92, 246, 0.35)';
        default: return '0 8px 32px rgba(251, 146, 60, 0.35)';
      }
    }};
  }
  
  &:active {
    transform: translateY(0px);
  }
`;

const ServiceIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 8px;
  color: ${(props) => {
    switch (props.serviceType) {
      case 'real_estate': return '#60a5fa';
      case 'employment': return '#fb923c';
      case 'english_academy': return '#a78bfa';
      default: return '#fb923c';
    }
  }};
`;

const ServiceTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${(props) => {
    switch (props.serviceType) {
      case 'real_estate': return '#60a5fa';
      case 'employment': return '#fb923c';
      case 'english_academy': return '#a78bfa';
      default: return '#fb923c';
    }
  }};
  margin-bottom: 6px;
`;

const ServiceDescription = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.5;
  margin-bottom: 14px;
`;

const ServiceButton = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: ${(props) => {
    switch (props.serviceType) {
      case 'real_estate': return 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)';
      case 'employment': return 'linear-gradient(135deg, #fb923c 0%, #ea580c 100%)';
      case 'english_academy': return 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)';
      default: return 'linear-gradient(135deg, #fb923c 0%, #ea580c 100%)';
    }
  }};
  color: #fff;
  padding: 10px 18px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 13px;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
`;

const VideoCallContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  width: 100%;
`;

const VideoCallTitle = styled.div`
  font-size: 14px;
  font-weight: normal;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 4px;
  line-height: 1.5;
  word-wrap: break-word;
  overflow-wrap: break-word;
`;

const Timestamp = styled.div`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.4);
  margin-top: 10px;
  font-weight: 500;
  letter-spacing: 0.2px;
`;

// Main Component
const ChatMessage = ({ message, type, timestamp, currentUser, conversationUsers, messageIndex, conversationId, onFlagMessage }) => {
  const { showToast } = useToast();
  let contentToRender;

  // Helper function to capitalize first letter of names (except iMavenAi)
  const capitalizeName = (name) => {
    if (!name) return name;
    if (name === 'iMavenAi') return 'iMavenAi'; // Preserve exact branding
    
    // Capitalize first letter of each word
    return name.split(' ').map(word => {
      if (word.length === 0) return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    }).join(' ');
  };

  // Determine sender name based on message type and data
  const getSenderName = () => {
    // Special handling for video call messages - always show iMavenAi regardless of other properties
    if (type === 'video-call' || (message.content && message.content.includes('/video-call/'))) {
      return 'iMavenAi';
    }
    
    // Special handling for whiteboard messages
    if (type === 'whiteboard' || (message.content && message.content.includes('/whiteboard/'))) {
      return 'iMavenAi';
    }
    
    switch (type) {
      case 'user':
        // Try to find the user in conversation participants first
        if (conversationUsers) {
          const user = (message.userId && conversationUsers.find(u => u.id === message.userId))
            || conversationUsers.find(u => u.type === 'user');
          if (user) {
            const userName = user.userName || user.name || user.fullName || user.displayName || message.sender || 'User';
            return capitalizeName(userName);
          }
        }
        // Fallback to message sender info
        const userName = message.sender || message.userName || 'User';
        return capitalizeName(userName);
      case 'gpt':
        return 'Eve Virtual Assistant';
      case 'system':
        return 'iMavenAi';
      case 'expert':
        // Prefer the author name stamped on the message. A SUBCONTRACTOR who replied on a
        // shared case is NOT in conversationUsers, so the participant lookup below can't
        // resolve them — senderName is what makes advisers see the real author.
        if (message.senderName) {
          return capitalizeName(message.senderName);
        }
        // Try to find the expert in conversation participants
        if (message.userId && conversationUsers) {
          const expert = conversationUsers.find(u => u.id === message.userId);
          if (expert) {
            const expertName = expert.userName || expert.name || expert.fullName || expert.displayName || 'Adviser';
            return capitalizeName(expertName);
          }
        }
        // Check if it's the current user (when they are the expert/adviser)
        if (message.userId === currentUser?.sub || message.sender === currentUser?.sub) {
          const currentUserName = currentUser?.name || 'You';
          return capitalizeName(currentUserName);
        }
        // Fallback to message sender info
        const expertName = message.sender || message.expertName || 'Adviser';
        return capitalizeName(expertName);
      case 'widget-upload':
        return 'iMavenAi';
      case 'video-call':
        return 'iMavenAi';
      default:
        return 'iMavenAi';
    }
  };

  const senderName = getSenderName();

  // Determine icon URL for the message type
  let iconUrl;
  switch (type) {
    case 'user':
      iconUrl = UserChatIcon;
      break;
    case 'gpt':
      iconUrl = ChatBotIcon;
      break;
    case 'system':
    case 'video-call':
      iconUrl = SystemIcon;
      break;
    case 'expert':
      iconUrl = ExpertChatIcon;
      break;
    case 'widget-upload':
      iconUrl = WidgetUploadIcon;
      break;
    default:
      iconUrl = ChatBotIcon;
  }

  // Helper function to trigger download programmatically
  const triggerDownload = async (url, fileName) => {
    try {
      const response = await fetch(url, {
        method: 'GET',
      });
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading the file:', error);
      showToast('Failed to download the file.', 'error');
    }
  };

  if (message.fileName && message.fileType && message.content) {
    const fileUrl = message.content; // Presigned URL
    const fileName = decodeURIComponent(message.fileName.split('/').pop());
    const fileType = message.fileType;

    let fileIcon; // Determine which icon to use
    if (fileType.startsWith('image/')) {
      fileIcon = ImageIcon;
    } else if (fileType === 'application/pdf') {
      fileIcon = PDFIcon;
    } else if (fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
      fileIcon = XLSXIcon;
    } else if (fileType === 'application/json') {
      fileIcon = JSONIcon;
    } else if (fileType === 'text/markdown') {
      fileIcon = MDIcon;
    } else if (fileType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation') {
      fileIcon = PPTXIcon;
    } else if (fileType === 'application/vnd.ms-excel') {
      fileIcon = ExcelIcon;
    } else if (fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
      fileIcon = WordIcon;
    } else if (fileType === 'application/xml') {
      fileIcon = XMLIcon;
    } else if (fileType === 'text/html' || fileName.endsWith('.html')) {
      fileIcon = FileIcon; // HTML report - use generic file icon (or could add HTMLIcon)
    } else {
      fileIcon = FileIcon; // Generic icon for unsupported types
    }

    // Check if file should open in new tab instead of download (HTML, etc.)
    const shouldOpenInNewTab = fileType === 'text/html' || fileName.endsWith('.html');

    // Render file preview with the determined icon
    contentToRender = (
      <FilePreview>
        {shouldOpenInNewTab ? (
          // HTML files - open in new tab
          <a href={fileUrl} target="_blank" rel="noopener noreferrer">
            <img src={fileIcon} alt={`${fileType} Icon`} />
            <span>📄 View {fileName}</span>
          </a>
        ) : (
          // Other files - download
          <a href={fileUrl} download={fileName}>
            <img src={fileIcon} alt={`${fileType} Icon`} />
            <span
              onClick={(e) => {
                e.preventDefault();
                triggerDownload(fileUrl, fileName);
              }}
            >
              Download {fileName}
            </span>
          </a>
        )}
      </FilePreview>
    );
  } else {
    // For regular messages, use dangerouslySetInnerHTML for both plain text and highlighted content
    
    // Document request (read-only card for the adviser; they manage attachments in the Program tab)
    if (type === 'document_request' || isDocumentRequestContent(message.content)) {
      let reqData;
      try { reqData = typeof message.content === 'string' ? JSON.parse(message.content) : message.content; }
      catch { reqData = { items: [] }; }
      const items = Array.isArray(reqData.items) ? reqData.items : [];
      return (
        <MessageContainer type="expert">
          <div style={{
            padding: '14px 16px', maxWidth: 360, minWidth: 260,
            background: 'linear-gradient(135deg, rgba(59,130,246,0.15) 0%, rgba(37,99,235,0.2) 100%)',
            border: '1px solid rgba(59,130,246,0.4)', borderRadius: 14, marginLeft: 10
          }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#60a5fa', marginBottom: 8 }}>📎 Documents requested from client</div>
            {reqData.message ? <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)', marginBottom: 10 }}>{reqData.message}</div> : null}
            {items.map((it) => (
              <div key={it.requirementKey} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', marginBottom: 6, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8 }}>
                <span style={{ flex: 1, fontSize: 13, color: '#e8eaed' }}>{it.name}</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: it.status === 'fulfilled' ? '#10b981' : '#fbbf24' }}>
                  {it.status === 'fulfilled' ? '✓ Attached' : 'Pending'}
                </span>
              </div>
            ))}
            <Timestamp>
              {new Date(timestamp).toLocaleString([], { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </Timestamp>
          </div>
          <Icon src={ExpertChatIcon} alt="expert icon" type="expert" />
        </MessageContainer>
      );
    }

    // CHECK SERVICE QUESTIONNAIRE FIRST - by content structure, regardless of type
    // This ensures service questionnaire messages render correctly even if type is 'expert'
    if (type?.startsWith('service_questionnaire_') || isServiceQuestionnaireContent(message.content)) {
      // Handle service questionnaire as a special clickable bubble
      let questionnaireData;
      try {
        questionnaireData = typeof message.content === 'string' ? JSON.parse(message.content) : message.content;
      } catch {
        questionnaireData = { serviceName: 'Service Questionnaire', serviceIcon: '📋' };
      }

      const getServiceDescription = (serviceType) => {
        switch (serviceType) {
          case 'real_estate':
            return 'Click to answer questions about your housing preferences and find the perfect home.';
          case 'employment':
            return 'Click to complete your professional profile and get matched with job opportunities.';
          case 'english_academy':
            return 'Click to assess your language needs and find the right English courses for you.';
          default:
            return 'Click to complete this questionnaire.';
        }
      };

      const handleQuestionnaireClick = () => {
        if (questionnaireData.sessionUrl) {
          window.open(questionnaireData.sessionUrl, '_blank', 'noopener,noreferrer');
        }
      };

      return (
        <MessageContainer type="expert">
          <ServiceQuestionnaireBubble 
            type="expert" 
            serviceType={questionnaireData.serviceType}
            onClick={handleQuestionnaireClick}
          >
            <SenderName type="expert">{getServiceIconSmall(questionnaireData.serviceType)} {questionnaireData.serviceName || 'Service Questionnaire'}</SenderName>
            <ServiceIconWrapper serviceType={questionnaireData.serviceType}>{getServiceIconComponent(questionnaireData.serviceType)}</ServiceIconWrapper>
            <ServiceTitle serviceType={questionnaireData.serviceType}>
              {questionnaireData.serviceName || 'Service Questionnaire'}
            </ServiceTitle>
            <ServiceDescription>
              {getServiceDescription(questionnaireData.serviceType)}
            </ServiceDescription>
            <ServiceButton serviceType={questionnaireData.serviceType}>
              <span>Start Questionnaire</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </ServiceButton>
            <Timestamp>
              {new Date(timestamp).toLocaleString([], {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </Timestamp>
          </ServiceQuestionnaireBubble>
          <Icon src={ExpertChatIcon} alt="expert icon" type="expert" />
        </MessageContainer>
      );
    } else if (type === 'video-call') {
      // Handle video call as a special clickable bubble
      const handleVideoCallClick = () => {
        const videoCallUrl = `${window.location.origin}${message.content}`;
        window.open(videoCallUrl, '_blank', 'noopener,noreferrer');
      };

      return (
        <MessageContainer type={type}>
          {type !== 'expert' && <Icon src={iconUrl} alt={`${type} icon`} type={type} />}
          
          <VideoCallBubble type={type} onClick={handleVideoCallClick}>
            <SenderName type={type}>{senderName}</SenderName>
            <VideoCallContent>
              <VideoCallTitle>Click here to join video call</VideoCallTitle>
            </VideoCallContent>
            <Timestamp>
              {new Date(timestamp).toLocaleString([], {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </Timestamp>
          </VideoCallBubble>

          {type === 'expert' && <Icon src={iconUrl} alt={`${type} icon`} type={type} />}
        </MessageContainer>
      );
    } else if (type === 'whiteboard') {
      // Handle whiteboard as a special clickable bubble
      const handleWhiteboardClick = () => {
        const whiteboardUrl = `${window.location.origin}${message.content}`;
        window.open(whiteboardUrl, '_blank', 'noopener,noreferrer');
      };

      return (
        <MessageContainer type={type}>
          {type !== 'expert' && <Icon src={iconUrl} alt={`${type} icon`} type={type} />}
          
          <WhiteboardBubble type={type} onClick={handleWhiteboardClick}>
            <SenderName type={type}>{senderName}</SenderName>
            <VideoCallContent>
              <VideoCallTitle>🎨 Click to join whiteboard</VideoCallTitle>
            </VideoCallContent>
            <Timestamp>
              {new Date(timestamp).toLocaleString([], {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </Timestamp>
          </WhiteboardBubble>

          {type === 'expert' && <Icon src={iconUrl} alt={`${type} icon`} type={type} />}
        </MessageContainer>
      );
    } else if (type === 'payment_request') {
      // Handle payment request as a special bubble with payment info
      let paymentData;
      try {
        paymentData = typeof message.content === 'string' ? JSON.parse(message.content) : message.content;
      } catch {
        paymentData = { amount: 0, currency: 'USD', description: 'Payment Request' };
      }

      const formatCurrency = (amount, currency) => {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: currency || 'USD'
        }).format(amount);
      };

      return (
        <MessageContainer type="expert">
          <PaymentRequestBubble type="expert">
            <SenderName type="expert">💳 Payment Request</SenderName>
            <PaymentAmount>{formatCurrency(paymentData.amount, paymentData.currency)}</PaymentAmount>
            <PaymentDescription>{paymentData.description}</PaymentDescription>
            {paymentData.expiresAt && (
              <PaymentExpiry>
                Expires: {new Date(paymentData.expiresAt).toLocaleDateString()}
              </PaymentExpiry>
            )}
            {paymentData.paymentPageUrl && (
              <PaymentButton href={paymentData.paymentPageUrl} target="_blank" rel="noopener noreferrer">
                Pay Now
              </PaymentButton>
            )}
            <Timestamp>
              {new Date(timestamp).toLocaleString([], {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </Timestamp>
          </PaymentRequestBubble>
          <Icon src={ExpertChatIcon} alt="expert icon" type="expert" />
        </MessageContainer>
      );
    } else if (type === 'signature_request' || isSignatureRequestContent(message.content)) {
      // Handle signature request as a special bubble
      let signatureData;
      try {
        signatureData = typeof message.content === 'string' ? JSON.parse(message.content) : message.content;
      } catch {
        signatureData = { purpose: 'Signature Request', status: 'pending' };
      }

      const getStatusText = (status) => {
        switch (status) {
          case 'signed': return '✅ Signed';
          case 'declined': return '❌ Declined';
          case 'expired': return '⏰ Expired';
          case 'cancelled': return '🚫 Cancelled';
          default: return '⏳ Pending';
        }
      };

      return (
        <MessageContainer type="expert">
          <SignatureRequestBubble type="expert">
            <SenderName type="expert">✍️ Signature Request</SenderName>
            <SignaturePurpose>{signatureData.purpose}</SignaturePurpose>
            {signatureData.description && (
              <SignatureDescription>{signatureData.description}</SignatureDescription>
            )}
            <SignatureStatus status={signatureData.status || 'pending'}>
              {getStatusText(signatureData.status || 'pending')}
            </SignatureStatus>
            {signatureData.signedAt && (
              <div style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.5)', marginTop: '8px' }}>
                Signed: {new Date(signatureData.signedAt).toLocaleDateString()}
              </div>
            )}
            <Timestamp>
              {new Date(timestamp).toLocaleString([], {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </Timestamp>
          </SignatureRequestBubble>
          <Icon src={ExpertChatIcon} alt="expert icon" type="expert" />
        </MessageContainer>
      );
    } else {
    contentToRender = (
      <div
        className="message-content"
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(stripEngineMarkers(message.content), {
            ALLOWED_TAGS: message.isHTML
              ? ['mark', 'div', 'span', 'a', 'br', 'p', 'strong', 'em', 'b', 'i', 'u', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'hr']
              : ['mark'],
            ALLOWED_ATTR: message.isHTML
              ? ['style', 'href', 'target', 'class', 'id', 'src', 'alt', 'title', 'rel']
              : ['style']
          })
          }}
        />
      );
    }
  }

  return (
    <MessageContainer type={type}>
      {type !== 'expert' && <Icon src={iconUrl} alt={`${type} icon`} type={type} />}

      <MessageBubble className="message-bubble" type={type}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <SenderName type={type}>{senderName}</SenderName>
          {type === 'gpt' && onFlagMessage && (
            <button
              onClick={() => onFlagMessage(messageIndex, message.content)}
              title="Flag this message and create a teaching"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '2px 4px',
                opacity: 0.4,
                fontSize: 14,
                lineHeight: 1,
                color: 'inherit',
                transition: 'opacity 0.2s'
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = '1'}
              onMouseLeave={e => e.currentTarget.style.opacity = '0.4'}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"></path>
                <line x1="4" y1="22" x2="4" y2="15"></line>
              </svg>
            </button>
          )}
        </div>
        {contentToRender}
        <Timestamp>
          {new Date(timestamp).toLocaleString([], {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </Timestamp>
      </MessageBubble>

      {type === 'expert' && <Icon src={iconUrl} alt={`${type} icon`} type={type} />}
    </MessageContainer>
  );
};

export default ChatMessage;
